import React, { useState, useEffect } from 'react';
import TourConfigBreadcrumbs from '@/features/dashboard/tourConfig/components/TourConfigBreadcrumbs.tsx';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog.tsx';
import TourForm from '@/features/dashboard/tourConfig/components/TourForm.tsx';
import {
  TourFormData,
  TourFormInputData,
  tourSchema,
} from '@/features/dashboard/tourConfig/schemas/tourSchema.ts';
import { useTourBuilderContext } from '@/features/dashboard/tourConfig/contexts/TourBuilderContext.tsx';
import TourConfigHeader from '@/features/dashboard/tourConfig/components/TourConfigHeader.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { debugZodResolver } from '@/lib/debugZodResolver';
import { useQuery } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { editTourBasicsRoute } from '@/features/dashboard/tourConfig/routes/tourConfigRoutes.ts';
import useUpdateTour from '@/features/dashboard/tourConfig/hooks/useUpdateTour.ts';
import toursApi from '@/features/dashboard/tours/api/toursApi.ts';
import TourDto from '@/features/dashboard/tourConfig/types/TourDto.ts';
import { tourDtoMapper } from '@/features/dashboard/tourConfig/types/TourDto.mapper.ts';

// Simple deep comparison for form data
const isFormDataEqual = (
  a: TourFormInputData | null,
  b: TourFormInputData | null,
): boolean => {
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const EditTourBasicsPage = () => {
  const navigate = useNavigate();
  const { tourId } = editTourBasicsRoute.useParams();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [isTourSaved, setIsTourSaved] = useState(false);
  const [initialFormData, setInitialFormData] =
    useState<TourFormInputData | null>(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  const handleConfirmDiscard = () => {
    if (navigateTo) {
      navigate({ to: navigateTo });
    }
    setShowDiscardDialog(false);
  };

  const handleCancelDiscard = () => {
    setShowDiscardDialog(false);
    setNavigateTo(null);
  };

  const { mutate: updateTour, isPending: isUpdating } = useUpdateTour();
  const { updateTourBuilderState } = useTourBuilderContext();

  // Fetch existing tour data in edit mode
  const {
    data: existingTour,
    isLoading: isFetching,
    error,
  } = useQuery({
    queryKey: ['tour', tourId],
    queryFn: () => toursApi.getTour(tourId!),
    retry: 1,
  });

  const createTourForm = useForm<TourFormInputData>({
    resolver: debugZodResolver(tourSchema),
  });

  // Update form with existing data when loaded
  useEffect(() => {
    if (existingTour) {
      const formData = tourDtoMapper.fromDtoToForm(existingTour);
      createTourForm.reset(formData);
      setInitialFormData(formData);
      setIsTourSaved(true);
      updateTourBuilderState({ tour: existingTour });
    } else {
      setIsTourSaved(false);
    }
  }, [existingTour, createTourForm, updateTourBuilderState]);

  // Watch for form changes
  const formData = createTourForm.watch();

  useEffect(() => {
    if (initialFormData) {
      const hasChanges = !isFormDataEqual(formData, initialFormData);
      setHasFormChanges(hasChanges);
    }
  }, [formData, initialFormData]);

  const handleTourSubmit = async (tourData: TourFormData) => {
    updateTour(
      { tourId: tourId!, data: tourData },
      {
        onSuccess: (tour: TourDto) => {
          updateTourBuilderState({ tour });
          setInitialFormData(tourData);
          setHasFormChanges(false);
          setIsTourSaved(true);
        },
      },
    );
  };

  const handleNext = () => {
    if (isTourSaved) {
      const tourId = existingTour?.id || createTourForm.getValues().tourName;
      navigate({ to: `/app/tours/${tourId}/config/costs` });
    }
  };

  const handleCancel = () => {
    navigate({ to: '/app/tours' });
  };

  const isLoading = isUpdating;

  // Loading state while fetching existing tour
  if (isFetching) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader className="h-6 w-6 animate-spin" />
        <span className="ml-2">Ładowanie danych wyjazdu...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Nie udało się załadować danych wyjazdu</p>
          <button
            onClick={() => navigate({ to: '/app/tours' })}
            className="mt-4 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Powrót do listy wyjazdów
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TourConfigBreadcrumbs />
      <FormProvider {...createTourForm}>
        <TourConfigHeader
          title={'Edytuj wyjazd'}
          submit={createTourForm.handleSubmit(handleTourSubmit)}
          isLoading={isLoading}
          submitText={'Zapisz zmiany'}
          onCancel={handleCancel}
          onNext={handleNext}
          showSaveButton={hasFormChanges}
          showNextButton={isTourSaved && !hasFormChanges}
        />
        <TourForm
          onSubmit={handleTourSubmit}
          isLoading={isLoading}
          isEditMode={true}
          onNext={handleNext}
          showSaveButton={hasFormChanges}
          showNextButton={isTourSaved && !hasFormChanges}
        />
        <ConfirmDialog
          open={showDiscardDialog}
          onOpenChange={setShowDiscardDialog}
          title="Odrzucić zmiany?"
          description="Powrót do tej strony spowoduje, że zmiany wprowadzone w wyjeździe i formularzu nie zostaną zapisane. Czy jesteś pewien, że chcesz kontynuować?"
          confirmText="Odrzuć zmiany"
          onConfirm={handleConfirmDiscard}
          onCancel={handleCancelDiscard}
        />
      </FormProvider>
    </div>
  );
};

export default EditTourBasicsPage;
